import type { NewPendingMessage } from "@/components/ai/chat/ChatInput";
import { extractPendingInteractiveBlock } from "@/components/ai/pending-interactive-utils";
import type { PendingInteractiveFragment } from "@/components/ai/pending-interactive-utils";
import { useEnvironmentStartCount } from "@/hooks/use-environment-start-count";
import { useInjectedMessages } from "@/hooks/use-injected-messages";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useSendInteractiveMessage } from "@/hooks/use-send-interactive-message";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import type { AvailableCommand, LastBlockActivityContext, Message } from "@/hooks/use-stream-conversation";
import { useStuckConversationDetection } from "@/hooks/use-stuck-conversation-detection";
import { useWelcomeAnchors } from "@/hooks/use-welcome-anchors";
import { useWelcomeContent } from "@/hooks/use-welcome-content";
import { useStartEnvironment } from "@/queries/environment-queries";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useCallback, useEffect, useMemo } from "react";

export type ConversationState = {
    error: Error | undefined;
    loadingMessage: string | undefined;
    messages: Message[];
    streamedMessages: Message[];
    availableCommands: AvailableCommand[];
    lastAgentBlockContext: LastBlockActivityContext | undefined;
    pendingInteractiveBlock: PendingInteractiveFragment | null;
    handleNewMessage: (message: NewPendingMessage, existingMessages: Message[]) => void;
    handleSendMessage: (text: string) => Promise<void>;
    handleStartEnvironment: () => Promise<void>;
    isStartingEnvironment: boolean;
    environment: PlainEnvironment | undefined;
};

/**
 * Encapsulates the conversation hooks shared by both the original and
 * super-ona conversation components: streaming, pending messages,
 * welcome messages, stuck detection, and environment start.
 */
export function useConversationState(
    agentExecution: PlainAgentExecution | null,
    environmentId: string | undefined,
): ConversationState {
    const startEnvironment = useStartEnvironment();
    const { pendingMessages, setPendingMessages, handleNewMessage } = useOptimisticPendingMessages();

    // Clear pending messages when switching executions so stale optimistic
    // messages from the previous execution don't briefly appear.
    const executionId = agentExecution?.id;
    useEffect(() => {
        setPendingMessages([]);
    }, [executionId, setPendingMessages]);

    const {
        error,
        messages: streamedMessages,
        availableCommands,
        loadingMessage,
        lastAgentBlockContext,
    } = useStreamConversation(agentExecution?.id, agentExecution?.status?.conversationUrl, pendingMessages);
    const { data: environment } = useEnvironment(environmentId);
    const { count: startCount } = useEnvironmentStartCount();
    const sessionId = environment?.status?.machine?.session;
    const welcomeAnchors = useWelcomeAnchors(streamedMessages, startCount, environmentId, sessionId);
    const welcomeContent = useWelcomeContent(welcomeAnchors, environment);
    const messages = useInjectedMessages(streamedMessages, welcomeAnchors, welcomeContent);
    useStuckConversationDetection(agentExecution, lastAgentBlockContext);

    const handleSendMessage = useSendInteractiveMessage(agentExecution?.id, handleNewMessage, messages);

    const pendingInteractiveBlock = useMemo(() => extractPendingInteractiveBlock(messages), [messages]);

    const handleStartEnvironment = useCallback(async () => {
        if (!environmentId) return;
        await startEnvironment.mutateAsync({ environmentId });
    }, [environmentId, startEnvironment]);

    return {
        error,
        loadingMessage,
        messages,
        streamedMessages,
        availableCommands,
        lastAgentBlockContext,
        pendingInteractiveBlock,
        handleNewMessage,
        handleSendMessage,
        handleStartEnvironment,
        isStartingEnvironment: startEnvironment.isPending,
        environment,
    };
}
